import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Footer from '@/components/feature/Footer';
import { useCart } from '@/hooks/useCart';
import type { CartItem } from '@/hooks/useCart';
import { useCurrency } from '@/hooks/useCurrency';
import { useDisplayFx } from '@/hooks/useDisplayFx';
import { convertUsdForDisplay } from '@/lib/displayFx';
import DualCurrencyMoney from '@/components/base/DualCurrencyMoney';
import { getProductDetail } from '@/lib/finishingPreviewService';
import type { ProductDetailData } from '@/lib/finishingPreviewService';
import ImageGallery from './components/ImageGallery';
import SpecsPanel from './components/SpecsPanel';
import ReviewSection from './components/ReviewSection';

const optionAccentMap: Record<string, string> = {
  'hot-stamping': 'bg-amber-500 text-white',
  'silk-screen': 'bg-rose-500 text-white',
  'uv-printing': 'bg-sky-500 text-white',
  frosting: 'bg-slate-500 text-white',
  'gradient-spray': 'bg-violet-500 text-white',
  'laser-engraving': 'bg-neutral-500 text-white',
};

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'details' | 'reviews'>('details');
  const [saved, setSaved] = useState(false);
  const { count: cartCount, addItem } = useCart();
  const { t } = useTranslation('products');
  const { currency } = useCurrency();
  const { rate } = useDisplayFx(currency);
  const [showCartToast, setShowCartToast] = useState(false);
  const [orderQuantity, setOrderQuantity] = useState(1);
  const [sampleQuantity, setSampleQuantity] = useState(0);
  const [sampleOnly, setSampleOnly] = useState(false);
  const [selectedFinishingIds, setSelectedFinishingIds] = useState<Set<string>>(new Set());

  const [product, setProduct] = useState<ProductDetailData | null>(null);
  const [productLoading, setProductLoading] = useState(true);

  // Fetch actual product data from Supabase
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setProductLoading(true);

    async function fetchProduct() {
      const data = await getProductDetail(id!);
      if (!cancelled) {
        setProduct(data);
        // MOQ가 있으면 MOQ부터, 없으면 1부터 시작
        if (data && data.moq > 0) {
          setOrderQuantity(data.moq);
        } else {
          setOrderQuantity(1);
        }
        setProductLoading(false);
      }
    }

    fetchProduct();
    return () => { cancelled = true; };
  }, [id]);

  // Receive selected finishing options from preview page
  useEffect(() => {
    const state = location.state as { selectedFinishingIds?: string[] } | null;
    if (state?.selectedFinishingIds) {
      setSelectedFinishingIds(new Set(state.selectedFinishingIds));
      // Clear the state so it doesn't persist on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const selectedFinishingOptions = (product?.finishingOptions || []).filter((o) => selectedFinishingIds.has(o.id));

  const handleAddToCart = () => {
    if (!product) return;
    addItem({
      productId: product.id,
      productName: product.name || '',
      productImage: product.images?.[0] || '',
      sku: product.sku || '',
      quantity: orderQuantity,
      moq: product.moq || 0,
      moqUnit: product.moqUnit || 'pcs',
      material: product.material,
      capacity: product.capacity,
      categoryL1: product.categoryL1,
      categoryL2: product.categoryL2,
    });
    setShowCartToast(true);
    setTimeout(() => setShowCartToast(false), 2500);
  };

  const handleRequestQuote = () => {
    navigate(`/packaging/${id}/customize`, {
      state: {
        selectedFinishingIds: Array.from(selectedFinishingIds),
        orderQuantity: sampleOnly ? 0 : orderQuantity,
        sampleQuantity: sampleOnly ? sampleQuantity : 0,
      },
    });
  };

  const handleQuantityStep = (delta: number) => {
    setOrderQuantity((prev) => {
      const next = prev + delta;
      return next < 1 ? 1 : next;
    });
  };
  const handleSampleQuantityStep = (delta: number) => {
    setSampleQuantity((prev) => {
      const next = prev + delta;
      return next < 0 ? 0 : next > 50 ? 50 : next;
    });
  };

  const tabs = [
    { key: 'details' as const, label: t('detail.tabDetails'), icon: 'ri-file-text-line' },
    { key: 'reviews' as const, label: t('detail.tabReviews'), icon: 'ri-star-line' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <div className="w-full bg-white border-b border-ink-100">
        <div className="w-full px-4 md:px-6 lg:px-10 py-3">
          <div className="flex items-center gap-2 text-xs text-ink-400">
            <a href="/" className="hover:text-ink-600 transition-colors">{t('detail.breadcrumbHome')}</a>
            <i className="ri-arrow-right-s-line text-[10px]" />
            <a href="/packaging" className="hover:text-ink-600 transition-colors">{t('detail.breadcrumbPackaging')}</a>
            <i className="ri-arrow-right-s-line text-[10px]" />
            <span className="text-ink-600 truncate max-w-[300px]">{product?.name}</span>
          </div>
        </div>
      </div>

      {/* Cart Toast */}
      {showCartToast && (
        <div className="fixed top-20 right-6 z-50 animate-[fadeIn_0.3s_ease-out]">
          <div className="bg-ink-800 text-white text-sm px-5 py-3 rounded-lg flex items-center gap-3 shadow-lg">
            <i className="ri-check-fill text-emerald-400 text-base" />
            <span>{t('detail.addedToCart', { count: cartCount })}</span>
            <button
              onClick={() => setShowCartToast(false)}
              className="text-white/60 hover:text-white cursor-pointer ml-2"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        </div>
      )}

      <main className="w-full px-4 md:px-6 lg:px-10 py-6 md:py-8">
        {/* Loading State */}
        {productLoading && (
          <div className="flex items-center justify-center py-32">
            <div className="flex flex-col items-center gap-4">
              <div className="w-10 h-10 border-[3px] border-warm-200 border-t-warm-500 rounded-full animate-spin" />
              <p className="text-sm text-ink-400">{t('detail.loading')}</p>
            </div>
          </div>
        )}

        {/* Not Found State */}
        {!productLoading && !product && (
          <div className="flex items-center justify-center py-32">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="w-14 h-14 rounded-full bg-rose-100 flex items-center justify-center">
                <i className="ri-error-warning-line text-rose-500 text-2xl" />
              </div>
              <h3 className="text-lg font-bold text-ink-700">{t('detail.notFoundTitle')}</h3>
              <p className="text-sm text-ink-400">{t('detail.notFoundDesc')}</p>
              <button
                onClick={() => navigate('/products')}
                className="px-5 py-2.5 text-sm font-bold text-white bg-ink-700 rounded-lg hover:bg-ink-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('detail.backToList')}
              </button>
            </div>
          </div>
        )}

        {product && (
        <>
        {/* Product Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-10">
            {/* Image Gallery */}
            <div className="w-full lg:w-[480px] shrink-0">
              <ImageGallery images={product?.images} productName={product?.name} productId={product?.id} finishingOptions={product?.finishingOptions} />
            </div>

            {/* Product Info */}
            <div className="flex-1 min-w-0">
              {/* Category Badge */}
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {product?.categoryL1 && (
                  <span className="inline-block px-2.5 py-1 bg-cream-100 text-ink-500 text-[11px] font-medium rounded border border-ink-100">
                    {product.categoryL1}
                  </span>
                )}
                {product?.categoryL2 && (
                  <span className="inline-block px-2.5 py-1 bg-cream-100 text-ink-500 text-[11px] font-medium rounded border border-ink-100">
                    {product.categoryL2}
                  </span>
                )}
              </div>

              {/* Product Name & SKU */}
              <h1 className="text-xl md:text-2xl font-bold text-ink-700 mb-2 leading-snug">
                {product?.name}
              </h1>
              <p className="text-xs text-ink-400 mb-4">{t('products.card.sku', { sku: product?.sku ?? '—' })}</p>

              {/* Key Specs */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-5">
                <div className="flex items-center gap-1.5 text-sm">
                  <i className="ri-flask-line text-ink-400" />
                  <span className="text-ink-500">{product?.material}</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm">
                  <i className="ri-contrast-drop-2-line text-ink-400" />
                  <span className="text-ink-500">{product?.capacity}</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm">
                  <i className="ri-stack-line text-ink-400" />
                  <span className="text-ink-500">MOQ: {product?.moq > 0 ? `${product?.moq.toLocaleString()} ${product?.moqUnit}` : t('detail.inquiry')}</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm">
                  <i className="ri-time-line text-ink-400" />
                  <span className="text-ink-500">
                    {t('detail.leadTime')}: {product?.leadTimeMinDays && product.leadTimeMinDays > 0
                      ? product.leadTimeMaxDays && product.leadTimeMaxDays > 0
                        ? t('products.card.leadTimeDaysRange', { min: product.leadTimeMinDays, max: product.leadTimeMaxDays })
                        : t('products.card.leadTimeDaysSingle', { days: product.leadTimeMinDays })
                      : product?.leadTimeMaxDays && product.leadTimeMaxDays > 0
                        ? t('products.card.leadTimeDaysSingle', { days: product.leadTimeMaxDays })
                        : t('detail.inquiry')}
                  </span>
                </div>
              </div>

              {/* Selected Finishing Preview Badges */}
              {selectedFinishingOptions.length > 0 && (
                <div className="border border-warm-200 bg-warm-50/60 rounded-lg p-3 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-warm-700 flex items-center gap-1.5">
                      <i className="ri-brush-line text-sm" />
                      {t('detail.selectedFinishing', { count: selectedFinishingOptions.length })}
                    </span>
                    <button
                      onClick={() => {
                        setSelectedFinishingIds(new Set());
                        window.history.replaceState({}, document.title);
                      }}
                      className="text-[10px] text-warm-500 hover:text-warm-700 cursor-pointer underline"
                    >
                      {t('detail.reset')}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedFinishingOptions.map((opt) => (
                      <span
                        key={opt.id}
                        className={`px-2.5 py-1 rounded-full text-[10px] font-bold whitespace-nowrap ${optionAccentMap[opt.id] || 'bg-warm-500 text-white'}`}
                      >
                        {opt.name.split('(')[0].trim()}
                      </span>
                    ))}
                  </div>
                  <p className="text-[10px] text-warm-400 mt-2">
                    {t('detail.finishingNote')}
                  </p>
                </div>
              )}

              {/* Price — Reference + Sample reference + disclaimer */}
              <div className="bg-cream-50/70 rounded-lg p-4 border border-ink-100 mb-4">
                <span className="text-[11px] text-ink-400 block mb-2">
                  {t('detail.priceReference')}
                </span>

                {product?.displayPriceText ? (
                  <div className="flex items-baseline gap-2">
                    <DualCurrencyMoney
                      usdAmount={product.displayPriceText}
                      selectedAmount={convertUsdForDisplay(product.displayPriceText, rate)}
                      selectedCurrency={currency}
                      primaryClassName="text-2xl font-bold text-ink-700"
                      secondaryClassName="text-sm font-normal text-ink-400"
                    />
                    <span className="text-sm text-ink-400">
                      {t('detail.perUnit')}
                    </span>
                  </div>
                ) : (
                  <span className="text-lg font-bold text-ink-500">{t('detail.requestQuoteInquiry')}</span>
                )}

              </div>

              {/* Order Quantity */}
              {!sampleOnly && (
              <div className="border border-ink-100 rounded-lg p-4 mb-4 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold text-ink-700">{t('detail.orderQuantity')}</span>
                  <span className="text-xs text-ink-400">
                    {t('detail.priceEstimateNote')}
                  </span>
                </div>

                {/* Quantity Editor */}
                <div className="flex items-center gap-3 mb-3">
                  <button
                    onClick={() => handleQuantityStep(-1)}
                    disabled={orderQuantity <= 1}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-ink-200 text-ink-500 hover:bg-cream-50 hover:text-ink-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <i className="ri-subtract-line" />
                  </button>
                  <input
                    type="number"
                    value={orderQuantity}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (!isNaN(v) && v >= 1) setOrderQuantity(v);
                    }}
                    className="w-28 h-9 text-center text-sm font-bold text-ink-700 border border-ink-200 rounded-lg focus:outline-none focus:border-warm-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <button
                    onClick={() => handleQuantityStep(1)}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-ink-200 text-ink-500 hover:bg-cream-50 hover:text-ink-700 transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line" />
                  </button>
                  <span className="text-xs text-ink-400 ml-1">{product?.moqUnit}</span>
                </div>

                {/* MOQ Warning */}
                {product?.moq > 0 && orderQuantity < product.moq && (
                  <div className="flex items-start gap-2 mb-3 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-error-warning-line text-amber-500 text-sm" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-amber-700">
                        {t('detail.moqBelowTitle')}
                      </p>
                      <p className="text-[11px] text-amber-600 mt-0.5 leading-relaxed">
                        {t('detail.moqBelowDesc', { moq: `${product.moq.toLocaleString()} ${product.moqUnit}` })}
                      </p>
                    </div>
                  </div>
                )}

              </div>
              )}

              {/* Sample Only Checkbox */}
              <label className="flex items-center gap-2.5 mt-4 mb-5 px-3 py-3 rounded-lg border border-ink-100 bg-white cursor-pointer select-none hover:border-warm-300 transition-colors">
                <input
                  type="checkbox"
                  checked={sampleOnly}
                  onChange={(e) => setSampleOnly(e.target.checked)}
                  className="w-4 h-4 rounded border-ink-300 text-warm-500 focus:ring-warm-400 cursor-pointer"
                />
                <span className="text-sm font-medium text-ink-600">{t('detail.sampleOnly')}</span>
              </label>

              {sampleOnly && (
              <p className="text-[11px] text-ink-300 mt-2.5 leading-relaxed">
                {t('detail.samplePriceNote')}
              </p>
              )}

              {/* Sample Purchase */}
              {sampleOnly && (
              <div className="border border-ink-100 rounded-lg p-4 mb-5 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-bold text-ink-700">{t('detail.samplePurchase')}</span>
                    <span className="text-[11px] text-ink-400">{t('detail.sampleRange')}</span>
                  </div>
                </div>

                {product?.samplePriceText && (
                  <div className="flex items-center justify-between border-t border-ink-100 pt-2.5 mb-2.5">
                    <span className="text-xs font-medium text-ink-500">{t('detail.sampleBasePrice')}</span>
                    <span className="inline-flex items-baseline gap-1">
                      <DualCurrencyMoney
                        usdAmount={product.samplePriceText}
                        selectedAmount={convertUsdForDisplay(product.samplePriceText, rate)}
                        selectedCurrency={currency}
                        primaryClassName="text-sm font-bold text-ink-700"
                        secondaryClassName="text-xs font-normal text-ink-400"
                      />
                      <span className="text-xs font-normal text-ink-400"> / {t('detail.perUnit')}</span>
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => handleSampleQuantityStep(-1)}
                      disabled={sampleQuantity <= 0}
                      className="w-8 h-8 flex items-center justify-center rounded-md border border-ink-200 text-ink-500 hover:bg-cream-50 hover:text-ink-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <i className="ri-subtract-line" />
                    </button>
                    <input
                      type="number"
                      value={sampleQuantity}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (!isNaN(v) && v >= 0 && v <= 50) setSampleQuantity(v);
                      }}
                      className="w-20 h-8 text-center text-sm font-bold text-ink-700 border border-ink-200 rounded-md focus:outline-none focus:border-warm-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <button
                      onClick={() => handleSampleQuantityStep(1)}
                      disabled={sampleQuantity >= 50}
                      className="w-8 h-8 flex items-center justify-center rounded-md border border-ink-200 text-ink-500 hover:bg-cream-50 hover:text-ink-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <i className="ri-add-line" />
                    </button>
                    <span className="text-xs text-ink-400">{product?.moqUnit}</span>
                  </div>
                </div>
              </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleRequestQuote}
                  className="flex-[3] py-3 bg-ink-700 text-white text-sm font-bold rounded-lg hover:bg-ink-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-file-list-3-line" />
                  {t('detail.requestQuote')}
                </button>
                <button
                  onClick={handleAddToCart}
                  className="flex-[1.6] py-3 bg-warm-500 text-white text-sm font-bold rounded-lg hover:bg-warm-400 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 relative"
                >
                  <i className="ri-shopping-cart-2-line" />
                  {t('detail.addToCart')}
                  {cartCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white text-warm-500 text-[10px] font-bold rounded-full flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setSaved(!saved)}
                  className={`w-12 h-12 flex items-center justify-center rounded-lg border text-base transition-all cursor-pointer shrink-0 ${
                    saved
                      ? 'border-warm-500 bg-warm-50 text-warm-700'
                      : 'border-ink-200 text-ink-400 hover:bg-cream-50 hover:text-ink-600'
                  }`}
                  aria-label={t('detail.saveProduct')}
                >
                  <i className={`${saved ? 'ri-heart-fill text-warm-500' : 'ri-heart-line'}`} />
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                  }}
                  className="w-12 h-12 flex items-center justify-center rounded-lg border border-ink-200 text-ink-400 hover:bg-cream-50 hover:text-ink-600 transition-colors cursor-pointer shrink-0"
                  aria-label={t('detail.shareProduct')}
                >
                  <i className="ri-share-forward-line text-base" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Availability Note */}
        {product?.availabilityNote && (
          <p className="text-sm text-ink-500 leading-relaxed mb-6 whitespace-pre-line">
            {product.availabilityNote}
          </p>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-ink-100 mb-6">
          <nav className="flex items-center gap-1 -mb-px overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors cursor-pointer ${
                  activeTab === tab.key
                    ? 'border-warm-500 text-warm-700'
                    : 'border-transparent text-ink-400 hover:text-ink-600 hover:border-ink-200'
                }`}
              >
                <i className={tab.icon} />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'details' && (
            <div className="space-y-8">
              {/* Features */}
              {product?.features.length > 0 && (
              <div>
                <h3 className="text-base font-bold text-ink-700 mb-3">
                  {t('detail.features')}
                </h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {product?.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-ink-600">
                      <i className="ri-check-fill text-warm-500 mt-0.5 shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              )}

              {/* Specifications */}
              {product?.specifications.length > 0 && (
              <div>
                <h3 className="text-base font-bold text-ink-700 mb-3">
                  {t('detail.specifications')}
                </h3>
                <SpecsPanel specifications={product?.specifications} />
              </div>
              )}
            </div>
          )}

          {activeTab === 'reviews' && (
            <ReviewSection
              reviews={product?.reviews}
              tabLabel={t('detail.tabReviews')}
            />
          )}
        </div>
        </>
        )}
      </main>

      {/* Bottom CTA Section */}
      <div className="w-full bg-cream-50/70 border-t border-ink-100 py-10 px-4 md:px-6 lg:px-10">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={handleRequestQuote}
            className="w-full sm:w-auto py-3.5 px-8 bg-ink-700 text-white text-sm font-bold rounded-lg hover:bg-ink-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
          >
            <i className="ri-file-list-3-line" />
            {t('detail.requestQuote')}
          </button>
          <button
            onClick={handleAddToCart}
            className="w-full sm:w-auto py-3.5 px-8 bg-warm-500 text-white text-sm font-bold rounded-lg hover:bg-warm-400 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 relative"
          >
            <i className="ri-shopping-cart-2-line" />
            {t('detail.addToCart')}
            {cartCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white text-warm-500 text-[10px] font-bold rounded-full flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
}